<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useI18n } from 'vue-i18n';
import { useSubmitWorksheetStore } from '@/store/SubmitWorksheetStore';
import Select from 'primevue/select';

interface Props {
  quoteId: string;
  hotelIndex: number;
  optionIndex: number;
  defaultValue: string;
}
const props = defineProps<Props>();

const emits = defineEmits<{
  saveCategory: [];
  cancelEditCategory: [];
}>();

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const submitWorksheetStore = useSubmitWorksheetStore();
const selectedCategory = ref({});

const containsRunOfHouse = (checkString: string) => {
  return typeof checkString === 'string' && checkString.toLowerCase().includes('run of house');
};

const getDefaultValue = computed(() => {
  const convertValue = props.defaultValue?.replace(/ \(ROH\)$/, '').trim();
  const findLongName = runOfHouseList.value.find((item: any) => item.longName === convertValue) || '';
  return !containsRunOfHouse(props.defaultValue) ? runOfHouseList.value[0] : findLongName;
});

const runOfHouseList = ref([
  {
    code: 'ROH',
    longName: 'RUN OF HOUSE AI',
    longNameFr: 'RUN OF HOUSE AI',
    displayTitle: 'RUN OF HOUSE AI (ROH)'
  },
  {
    code: 'ROH',
    longName: 'RUN OF HOUSE BP',
    longNameFr: 'RUN OF HOUSE BP',
    displayTitle: 'RUN OF HOUSE BP (ROH)'
  },
  {
    code: 'ROH',
    longName: 'RUN OF HOUSE CP',
    longNameFr: 'RUN OF HOUSE CP',
    displayTitle: 'RUN OF HOUSE CP (ROH)'
  },
  {
    code: 'ROH',
    longName: 'RUN OF HOUSE EP',
    longNameFr: 'RUN OF HOUSE EP',
    displayTitle: 'RUN OF HOUSE EP (ROH)'
  },
  {
    code: 'ROH',
    longName: 'RUN OF HOUSE FAP',
    longNameFr: 'RUN OF HOUSE FAP',
    displayTitle: 'RUN OF HOUSE FAP (ROH)'
  },
  {
    code: 'ROH',
    longName: 'RUN OF HOUSE MAP',
    longNameFr: 'RUN OF HOUSE MAP',
    displayTitle: 'RUN OF HOUSE MAP (ROH)'
  }
]);

const handleSaveCategory = () => {
  const isEmpty = Object.keys(selectedCategory.value).length === 0;
  if (selectedCategory.value?.code === 'default' || isEmpty) {
    return emits('cancelEditCategory');
  }
  const payload = {
    quoteId: props.quoteId,
    quoteOption: props.optionIndex,
    hotelOption: props.hotelIndex,
    roomName: selectedCategory.value?.longName,
    roomCode: selectedCategory.value?.code
  };
  submitWorksheetStore
    .updateRohRoom(payload)
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.unexpected_error'), life: 5000 });
    })
    .finally(() => {
      emits('saveCategory');
    });
};

const convertOption = (optionData: any) => {
  return optionData.code === 'default' ? optionData.longName : `${optionData.longName} (${optionData.code})`;
};

const handleCancelEdit = () => {
  emits('cancelEditCategory');
};

onMounted(() => {
  if (!containsRunOfHouse(props.defaultValue)) {
    runOfHouseList.value.unshift({
      code: 'default',
      longName: props.defaultValue,
      longNameFr: props.defaultValue,
      displayTitle: props.defaultValue
    });
  }
});
</script>
<template>
  <div class="flex w-full select-roh">
    <div class="min-w-label">{{ $t('worksheet.hotel_room_category') }}:</div>
    <div class="w-7">
      <Select id="selectROH" :defaultValue="getDefaultValue" v-model="selectedCategory" :placeholder="$t('worksheet.hotel_room_category')" :options="runOfHouseList" optionLabel="displayTitle">
        <template #option="slotProps">
          <span>{{ convertOption(slotProps.option) }}</span>
        </template>
      </Select>
    </div>
    <div class="text-center ml-2 flex gap-2 align-items-center">
      <i class="pi pi-check cursor-pointer" @click="handleSaveCategory()"></i>
      <i class="pi pi-times cursor-pointer" @click="handleCancelEdit()"></i>
    </div>
  </div>
</template>
